import { getPersonName } from './messengerPeopleUtils';

export const resolveConversationId = (conversation) =>
  conversation?.id ?? conversation?.conversation_id ?? null;

export const resolveMessageId = (message) => message?.id ?? message?.message_id ?? null;

export const resolveMessageText = (message) => {
  if (message?.content != null) return message.content;
  return '';
};

export const resolveMessageTimestamp = (message) => message?.sent_at ?? null;

export const resolveMessageSenderId = (message) =>
  message?.sender_id ?? message?.senderId ?? message?.sender?.id ?? null;

export const resolveMessageSenderName = (message, eligibleById = {}) => {
  const senderId = resolveMessageSenderId(message);
  if (senderId != null) {
    const person = eligibleById[`${senderId}`];
    if (person) return getPersonName(person);
    return `User ${senderId}`;
  }
  return 'User';
};

const resolveParticipantEntries = (conversation) =>
  Array.isArray(conversation?.participants) ? conversation.participants : [];

const resolveParticipantIds = (conversation) => {
  return resolveParticipantEntries(conversation)
    .map((entry) => entry?.user_id)
    .filter((value) => value != null)
    .map((value) => `${value}`);
};

const resolveParticipantNames = (participantIds, eligibleById, currentUserId = null) =>
  participantIds
    .filter((id) => id && `${id}` !== `${currentUserId ?? ''}`)
    .map((id) => {
      const person = eligibleById[`${id}`];
      return person ? getPersonName(person) : `User ${id}`;
    })
    .filter(Boolean);

const resolveConversationTitle = (conversation, eligibleById, currentUserId = null) => {
  const conversationId = resolveConversationId(conversation);
  const participantIds = resolveParticipantIds(conversation);
  const isGroup = conversation?.type === 'group' || participantIds.length > 2;
  const explicit = `${conversation?.name ?? ''}`.trim();
  const participantNames = resolveParticipantNames(participantIds, eligibleById, currentUserId);

  if (isGroup) {
    if (explicit) return explicit;
    if (participantNames.length) return participantNames.join(', ');
    return `Conversation ${conversationId ?? ''}`.trim();
  }

  const peerId = participantIds.find((id) => `${id}` !== `${currentUserId ?? ''}`);
  if (peerId) {
    const person = eligibleById[`${peerId}`];
    return person ? getPersonName(person) : `User ${peerId}`;
  }
  if (explicit) return explicit;
  return 'Direct conversation';
};

const resolveUnreadCount = (conversation, currentUserId = null) => {
  const raw = Number(conversation?.unread_count ?? 0) || 0;
  const lastSenderId = conversation?.last_message?.sender_id;
  if (currentUserId != null && lastSenderId != null && `${lastSenderId}` === `${currentUserId}`) {
    return 0;
  }
  return raw;
};

const resolveLastMessage = (conversation, currentUserId = null) => {
  if (conversation?.last_message && typeof conversation.last_message === 'object') {
    const message = conversation.last_message;
    const text = resolveMessageText(message).trim();
    if (text) return text;

    if (
      message?.id != null ||
      message?.sent_at ||
      message?.encryptedContent ||
      message?.encrypted_content
    ) {
      const senderId = resolveMessageSenderId(message);
      const isOwnMessage =
        currentUserId != null && senderId != null && `${senderId}` === `${currentUserId}`;
      return isOwnMessage ? 'You sent a message' : 'New message';
    }

    return 'No messages yet';
  }
  return 'No messages yet';
};

const resolveLastMessageAt = (conversation) =>
  conversation?.last_message?.sent_at ?? conversation?.updated_at ?? null;

const parseBooleanish = (value) => {
  if (value == null || `${value}` === '') return null;
  const normalized = `${value}`.toLowerCase();
  if (value === true || value === 1 || normalized === 'true' || normalized === '1') return true;
  if (value === false || value === 0 || normalized === 'false' || normalized === '0') return false;
  return null;
};

const resolveIsSelf = (message, currentUserId) => {
  const explicit = parseBooleanish(message?.is_self) ?? parseBooleanish(message?.is_own);
  if (explicit === true) return true;

  const senderId = resolveMessageSenderId(message);
  if (currentUserId != null && senderId != null) {
    return `${senderId}` === `${currentUserId}`;
  }
  return explicit === true;
};

/**
 * Normalizes conversation shape from API variants into the UI model.
 * @param {any} conversation
 * @param {Record<string, any>} eligibleById
 * @returns {{
 *  renderKey: string,
 *  id: string,
 *  rawId: number|string|null,
 *  isGroup: boolean,
 *  title: string,
 *  participantIds: string[],
 *  preview: string,
 *  lastMessageAt: string|null,
 *  unreadCount: number,
 *  groupSubtitle: string
 * }}
 */
export const normalizeConversation = (conversation, eligibleById, currentUserId = null) => {
  const conversationId = resolveConversationId(conversation);
  const participantIds = resolveParticipantIds(conversation);
  const isGroup = conversation?.type === 'group' || participantIds.length > 2;
  const participantNames = resolveParticipantNames(participantIds, eligibleById, currentUserId);

  return {
    id: conversationId != null ? `${conversationId}` : '',
    rawId: conversationId,
    isGroup,
    title: resolveConversationTitle(conversation, eligibleById, currentUserId),
    participantIds,
    preview: resolveLastMessage(conversation, currentUserId),
    lastMessageAt: resolveLastMessageAt(conversation),
    unreadCount: resolveUnreadCount(conversation, currentUserId),
    groupSubtitle: participantNames.slice(0, 4).join(', '),
  };
};

export const normalizeMessagesForThread = (messageList, currentUserId, eligibleById) => {
  const list = (Array.isArray(messageList) ? messageList : []).map((message, index) => {
    const isSelf = resolveIsSelf(message, currentUserId);
    return {
      renderKey: `${message?.client_id ?? resolveMessageId(message) ?? `message-${index}`}`,
      id: resolveMessageId(message) ?? `message-${index}`,
      sender: resolveMessageSenderName(message, eligibleById) || 'User',
      text: resolveMessageText(message),
      sentAt: resolveMessageTimestamp(message),
      senderId: resolveMessageSenderId(message),
      contentType: message?.content_type ?? 'text',
      imageUrl: message?.metadata?.file?.url ?? null,
      imageFilename: message?.metadata?.file?.filename ?? null,
      deliveryStatus: message?.delivery_status ?? null,
      isSelf,
      isMine: isSelf,
      isReceived: !isSelf,
      direction: isSelf ? 'outbound' : 'inbound',
    };
  });

  list.sort((a, b) => {
    const aMs = a.sentAt ? new Date(a.sentAt).getTime() : Number.NaN;
    const bMs = b.sentAt ? new Date(b.sentAt).getTime() : Number.NaN;
    const aValid = Number.isFinite(aMs);
    const bValid = Number.isFinite(bMs);
    if (aValid && bValid && aMs !== bMs) return aMs - bMs;
    if (aValid && !bValid) return 1;
    if (!aValid && bValid) return -1;
    return `${a.id}`.localeCompare(`${b.id}`);
  });

  return list;
};
